import React, { useState, useCallback } from 'react';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from '@material-ui/core';
import { showMessage } from '../../../../../../actions/snackbar';
import { getAssetType } from '../../../../../../utils/strings';
import { avatarUpload, setCollectionImageUrl } from '../../../../../../actions/createAssets';
import DotsLoading from '../../../../../../components/DotsLoading';
import variables from '../../../../../../utils/variables';
import withRouter from '../../../../../../components/WithRouter';
import { getAbsoluteHash } from '../../../../../../utils/ipfs';
import { DEFAULT_UPLOAD_LIMIT } from '../../../../../../config';
import ImageCropper from '../../../../../../components/ImageCropper';
import { createImage } from '../../../../../../utils/image';

const Upload = (props) => {
    const [cropperOpen, setCropperOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const getCroppedImg = useCallback(async (imageSrc, pixelCrop) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('No 2d context');
        }

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height,
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/png');
        });
    }, []);

    const handleCropComplete = useCallback(async (croppedAreaPixels) => {
        if (!selectedFile || !croppedAreaPixels) { return; }

        try {
            const croppedImage = await getCroppedImg(URL.createObjectURL(selectedFile), croppedAreaPixels);
            const croppedFile = new File([croppedImage], selectedFile.name, {
                type: 'image/png',
            });
            setCropperOpen(false);

            props.avatarUpload(croppedFile, (res) => {
                if (res) {
                    const url = getAbsoluteHash(res.Hash);
                    props.setCollectionImageUrl(url);
                }
            });
        } catch (e) {
            props.showMessage('Error cropping image', 'error');
            setCropperOpen(false);
        }
    }, [selectedFile, getCroppedImg, props]);

    const handleFileChange = (selectedFiles) => {
        const type = selectedFiles && selectedFiles.length && selectedFiles[0] && getAssetType(selectedFiles[0].type);
        const fileSize = selectedFiles && selectedFiles.length && selectedFiles[0].size;

        if (['image'].indexOf(type) === -1) {
            props.showMessage('File format not supported', 'warning');
            return;
        }

        if (fileSize > DEFAULT_UPLOAD_LIMIT) {
            props.showMessage(variables[props.lang].file_size_exceeded, 'warning');
            return;
        }

        if (selectedFiles && selectedFiles.length > 0 && selectedFiles[0]) {
            setSelectedFile(selectedFiles[0]);
            setCropperOpen(true);
        } else {
            props.showMessage('File Not Selected', 'warning');
        }
    };

    return (
        <div className="select_field_parent">
            {props.avatarInProgress
                ? <DotsLoading/>
                : <div
                    className="file_upload"
                    onDrop={(e) => {
                        e.preventDefault();
                        handleFileChange(e.dataTransfer.files);
                    }}>
                    <Button
                        variant="contained">
                        <input
                            accept={'image/*'}
                            type="file"
                            onChange={(e) =>
                                handleFileChange(e.target.files)
                            }/>
                        {(props.router && props.router.params && props.router.params.collectionID && props.imageUrl) ||
                        props.reUpload
                            ? variables[props.lang]['re_upload_file']
                            : variables[props.lang]['upload_file']}
                    </Button>
                </div>}
            <ImageCropper
                aspectRatio={1 / 1}
                image={selectedFile ? URL.createObjectURL(selectedFile) : ''}
                open={cropperOpen}
                onClose={() => setCropperOpen(false)}
                onCropComplete={handleCropComplete}
            />
        </div>
    );
};

Upload.propTypes = {
    avatarInProgress: PropTypes.bool.isRequired,
    avatarUpload: PropTypes.func.isRequired,
    lang: PropTypes.string.isRequired,
    setCollectionImageUrl: PropTypes.func.isRequired,
    showMessage: PropTypes.func.isRequired,
    imageUrl: PropTypes.any,
    reUpload: PropTypes.bool,
    router: PropTypes.shape({
        params: PropTypes.shape({
            collectionID: PropTypes.string,
        }).isRequired,
    }),
};

const stateToProps = (state) => {
    return {
        avatarInProgress: state.createAssets.avatar.inProgress,
        lang: state.language,
        imageUrl: state.createAssets.createCollection.imageUrl,
    };
};

const actionsToProps = {
    avatarUpload,
    showMessage,
    setCollectionImageUrl,
};

export default withRouter(connect(stateToProps, actionsToProps)(Upload));
